import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { get } from "./api";
import { useAuth } from "./auth";

/**
 * Per-tenant plan + feature flags.
 *
 * Single fetch on mount + after every login/tenant-switch (the auth
 * provider bumps a refresh trigger via `useAuth().user?.id`). The
 * effective feature set is exposed to consumers via:
 *
 *   const { has, plan, isSuperAdmin } = usePlan();
 *   if (!has('water_compliance.programmes')) return null;
 *
 * `useFeatureGate(slug)` is a sugar wrapper for the common case.
 *
 * Two universal "open" cases:
 *   - Super admin → bypasses every gate so the operator can inspect
 *     any tenant's screens regardless of plan.
 *   - Unconfigured tenant (no subscription row yet, e.g. fresh install
 *     before the seeder runs) → also open. Treat-as-pro semantics keep
 *     the system usable while operators set up plan rows.
 */

export interface PlanInfo {
  slug: string;
  name: string;
  description: string | null;
}

export interface PlanContextValue {
  loading: boolean;
  plan: PlanInfo | null;
  features: Record<string, boolean | number | null>;
  status: string | null;
  trialEndsAt: string | null;
  unconfigured: boolean;
  isSuperAdmin: boolean;
  has: (slug: string) => boolean;
  /** Numeric quotas like `max_sites` — returns the raw number (0 = unlimited). */
  quota: (slug: string) => number | null;
  refresh: () => Promise<void>;
}

interface SubscriptionResponse {
  plan: PlanInfo | null;
  features?: Record<string, unknown>;
  status?: string;
  trial_ends_at?: string | null;
  unconfigured?: boolean;
  is_super_admin?: boolean;
}

const PlanContext = createContext<PlanContextValue | null>(null);

export function PlanProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [resp, setResp] = useState<SubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setResp(null); setLoading(false); return; }
    try {
      const data = await get<SubscriptionResponse>("/v1/me/subscription");
      setResp(data);
    } catch {
      // Endpoint failures shouldn't bring the app down; default to
      // "everything open" so the user can keep working while we recover.
      setResp({ plan: null, features: {}, unconfigured: true, is_super_admin: !!user.is_super_admin });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<PlanContextValue>(() => {
    const featureMap: Record<string, boolean | number | null> = {};
    const f = resp?.features ?? {};
    for (const [k, v] of Object.entries(f)) {
      if (typeof v === "boolean" || typeof v === "number" || v === null) {
        featureMap[k] = v as boolean | number | null;
      }
    }
    const isSuperAdmin = !!(resp?.is_super_admin ?? user?.is_super_admin);
    const unconfigured = !!resp?.unconfigured;

    return {
      loading,
      plan: resp?.plan ?? null,
      features: featureMap,
      status: resp?.status ?? null,
      trialEndsAt: resp?.trial_ends_at ?? null,
      unconfigured,
      isSuperAdmin,
      has: (slug: string): boolean => {
        // Two open cases: super admin and unconfigured tenant.
        if (isSuperAdmin) return true;
        if (unconfigured) return true;
        const v = featureMap[slug];
        if (typeof v === "boolean") return v;
        if (typeof v === "number")  return v > 0;
        return false;
      },
      quota: (slug: string): number | null => {
        const v = featureMap[slug];
        return typeof v === "number" ? v : null;
      },
      refresh,
    };
  }, [resp, loading, refresh, user]);

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan(): PlanContextValue {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error("usePlan() must be used inside <PlanProvider>");
  return ctx;
}

/**
 * Convenience: returns true iff the active plan grants this feature.
 * Falls open for super admins and unconfigured tenants.
 */
export function useFeatureGate(slug: string): boolean {
  return usePlan().has(slug);
}
